import {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input/max";
import { Metadata } from "libphonenumber-js/max";
import arCountryNames from "react-phone-number-input/locale/ar.json";
import enCountryNames from "react-phone-number-input/locale/en.json";
import { LANGUAGE } from "services/general";
import { DEFAULT_PHONE_COUNTRY } from "./constants";

/*
 * Phone numbers travel to and from the API as bare international digits — the
 * E.164 number without its "+": "966501234567". The control works in E.164
 * ("+966501234567"), so these helpers are the one place the two meet.
 *
 * Every check runs on the "max" libphonenumber metadata. The smaller default set
 * only checks a number's length, so it accepts a nine-digit Saudi number starting
 * with 7 that no carrier issues — exactly the "wrong number passes" case.
 */

const digitsOf = (value) => String(value ?? "").replace(/\D/g, "");

const tryParse = (value, country) => {
  try {
    return parsePhoneNumber(value, country);
  } catch {
    return undefined;
  }
};

/** "+966" for "SA". */
export const dialCodeOf = (country) =>
  country ? `+${getCountryCallingCode(country)}` : "";

/**
 * The digit dialled before a local number inside the country and dropped after
 * the dialling code — "0" in Saudi Arabia ("050…" is "+966 50…"). Undefined
 * where there is none: an Italian number keeps its 0 after "+39".
 */
export const trunkPrefixOf = (country) => {
  if (!country) return undefined;
  const metadata = new Metadata();
  metadata.selectNumberingPlan(country);
  const prefix = metadata.numberingPlan.nationalPrefix();
  return typeof prefix === "string" && prefix ? prefix : undefined;
};

/**
 * Pasted text read as a number of `country` when it is a valid local one
 * ("0501234567"), as international otherwise ("+20…", "00966…", "966…").
 */
export const pastedToE164 = (text, country) => {
  const local = tryParse(text, country);
  if (local?.isValid()) return local.number;
  const international = tryParse(`+${digitsOf(text).replace(/^00/, "")}`);
  if (international?.isValid()) return international.number;
  return local?.number;
};

/**
 * A stored value → the E.164 number the control shows.
 *
 * Most rows already hold international digits, but older ones were saved as
 * typed — "0501234567", "00966…", "+966 50 …". Those are read as a number of
 * `defaultCountry` when they are not a valid international number, so the field
 * opens on the right country instead of on a "+050…" that belongs to nobody.
 */
export function storedToE164(stored, defaultCountry = DEFAULT_PHONE_COUNTRY) {
  const raw = String(stored ?? "").trim();
  const digits = digitsOf(raw);
  if (!digits) return undefined;

  const international = `+${digits.replace(/^00/, "")}`;
  if (raw.startsWith("+") || isValidPhoneNumber(international)) {
    return international;
  }

  const national = tryParse(raw, defaultCountry);
  return national?.isValid() ? national.number : international;
}

/**
 * The control's E.164 value → what the form stores and the API receives. A
 * dialling code with no number after it ("+20", which the control reports once
 * a country is picked) is an empty field, so `required` still catches it.
 * Calling codes are prefix-free, so a code followed by digits is never one.
 */
export const e164ToStored = (e164) => {
  if (!e164) return "";
  const digits = e164.replace(/^\+/, "");
  return new Metadata().hasCallingCode(digits) ? "" : digits;
};

export const phoneCountryOf = (e164) =>
  e164 ? tryParse(e164)?.country : undefined;

export const isValidE164 = (e164) => Boolean(e164) && isValidPhoneNumber(e164);

/** Country names in the page's language, keyed by ISO code. */
export const countryNames = () =>
  LANGUAGE === "ar" ? arCountryNames : enCountryNames;

export const countryNameOf = (country) => countryNames()[country] ?? country;

export { arCountryNames, enCountryNames };

/**
 * The format rule every phone field carries. An empty value passes — `required`
 * is a separate rule — and the message names the country the number was read
 * as, since a number valid for one country is often a wrong pick in the picker.
 */
export function validatePhoneFormat(value, { defaultCountry, t }) {
  if (!value) return Promise.resolve();

  const e164 = storedToE164(value, defaultCountry);
  if (isValidE164(e164)) return Promise.resolve();

  const country = phoneCountryOf(e164);
  return Promise.reject(
    new Error(
      country
        ? t("phoneInput.invalidFor", { country: countryNameOf(country) })
        : t("phoneInput.invalid")
    )
  );
}
